import type { ConversationAnchorType } from '@scoutaround/script-breakdown-shared';

import { ANCHOR_NOUN, useAnchorSummary, useConversationsEnabled, useDiscuss } from './hooks.js';

/**
 * The "Discuss" control on anything a thread can be about (ADR 0021): a speech
 * glyph, how many conversations there are, and a dot for something unread.
 * Opens the conversation drawer for that anchor. Renders nothing while the
 * platform has conversations switched off.
 *
 * `extraThreads` adds conversations carried over from an earlier draft of a
 * scene that is CERTAINLY this one (spec §6) — never a LIKELY match.
 */
export function ConversationEntry({
  projectId,
  anchorType,
  anchorId,
  label,
  extraThreads = 0,
  compact = false,
}: {
  projectId: string;
  anchorType: ConversationAnchorType;
  anchorId: string;
  label?: string;
  extraThreads?: number;
  compact?: boolean;
}) {
  // The switch first, and the router-bound part only when it is on: hooks may
  // not change order between renders, and a surface rendered outside the
  // workspace router (a test, a preview) must not need one while it is off.
  if (!useConversationsEnabled()) return null;
  return (
    <EnabledEntry
      projectId={projectId}
      anchorType={anchorType}
      anchorId={anchorId}
      label={label}
      extraThreads={extraThreads}
      compact={compact}
    />
  );
}

function EnabledEntry({
  projectId,
  anchorType,
  anchorId,
  label,
  extraThreads,
  compact,
}: {
  projectId: string;
  anchorType: ConversationAnchorType;
  anchorId: string;
  label?: string;
  extraThreads: number;
  compact: boolean;
}) {
  const summary = useAnchorSummary(projectId, anchorType, anchorId);
  const { open } = useDiscuss();

  const threads = (summary?.threads ?? 0) + extraThreads;
  const unread = summary?.unread ?? 0;
  const fresh = unread > 0 || summary?.hasNew === true || summary?.mentioned === true;
  const what = label ? `${ANCHOR_NOUN[anchorType]} “${label}”` : `this ${ANCHOR_NOUN[anchorType]}`;
  const name =
    threads === 0
      ? `Discuss ${what}`
      : `${String(threads)} conversation${threads === 1 ? '' : 's'} about ${what}${unread > 0 ? `, ${String(unread)} unread` : fresh ? ', something new' : ''}`;

  return (
    <button
      type="button"
      className="ctl"
      data-testid={`conversation-entry-${anchorType}-${anchorId}`}
      aria-label={name}
      title={name}
      onClick={(event) => {
        // Entries sit inside clickable cards and rows; opening a conversation is
        // not also opening the card.
        event.stopPropagation();
        open({ anchorType, anchorId, label });
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        minHeight: compact ? 28 : 32,
        padding: compact ? '2px 8px' : '4px 10px',
        fontSize: 12.5,
        position: 'relative',
      }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
        <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.6A8 8 0 1 1 21 12z" strokeLinejoin="round" />
      </svg>
      {threads > 0 ? <span data-testid={`conversation-entry-count-${anchorType}-${anchorId}`}>{threads}</span> : null}
      {!compact && threads === 0 ? <span>Discuss</span> : null}
      {fresh ? (
        <span
          data-testid={`conversation-entry-unread-${anchorType}-${anchorId}`}
          aria-hidden="true"
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: summary?.mentioned ? 'var(--color-accent)' : 'var(--ink)',
            display: 'inline-block',
          }}
        />
      ) : null}
    </button>
  );
}
